/**
 * KYC identity verification submission page.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { kycAPI } from '../api/kyc';
import { LoadingSkeleton } from '../components/ui/LoadingSkeleton';
import { ThemedPageHeader } from '../components/ui/ThemedPageHeader';
import { THEMED_SURFACE_COLORS as C, ThemedField, ThemedInput, ThemedNotice, ThemedPanel, ThemedSelect, ThemedSubmitButton, ThemedBottomActionBar } from '../components/ui/ThemedSurface';

const ID_TYPES = [
  { value: 'id_card', key: 'kyc.idCard' },
  { value: 'passport', key: 'kyc.passport' },
  { value: 'driver_license', key: 'kyc.driverLicense' },
];

const STATUS_KEYS: Record<string, { key: string; active?: boolean }> = {
  none: { key: 'kyc.statusNone' },
  pending: { key: 'kyc.statusPending', active: true },
  approved: { key: 'kyc.statusApproved', active: true },
  rejected: { key: 'kyc.statusRejected' },
};

function KycStatusPanel({ submission, statusInfo }: { submission: any; statusInfo: { key: string; active?: boolean } }) {
  const { t } = useTranslation();
  const active = Boolean(statusInfo.active);
  const statusColor = active ? C.gold : C.muted;

  return (
    <ThemedPanel className="p-0">
      <div className="relative px-4 py-4">
        <div className="pointer-events-none absolute inset-x-4 top-0 h-px" style={{ background: `linear-gradient(90deg, transparent 0%, ${active ? C.gold : C.softBorder} 50%, transparent 100%)` }} />
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="text-xs" style={{ color: C.muted }}>{t('kyc.certStatus')}</div>
            <div className="mt-1 truncate text-xl font-semibold leading-none" style={{ color: statusColor }}>{t(statusInfo.key)}</div>
          </div>
          <div className="flex size-12 shrink-0 items-center justify-center rounded-2xl border" style={{ borderColor: active ? `${C.gold}66` : C.softBorder, background: active ? C.chipBg : C.inputBg, color: statusColor, boxShadow: active ? `0 0 20px ${C.gold}18` : undefined }}>
            <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 3 5 6v5c0 4.2 2.7 8.1 7 10 4.3-1.9 7-5.8 7-10V6l-7-3Z" />
              <path strokeLinecap="round" strokeLinejoin="round" d={active ? 'm9 12 2 2 4-5' : 'M9.5 9.5 14.5 14.5M14.5 9.5 9.5 14.5'} />
            </svg>
          </div>
        </div>

        {submission?.reject_reason && (
          <div className="mt-4">
            <ThemedNotice tone="error">{t('kyc.rejectReason')}: {submission.reject_reason}</ThemedNotice>
          </div>
        )}

        {submission?.real_name && (
          <div className="mt-4 grid grid-cols-[1fr_auto_1fr] items-center rounded-xl border px-3 py-3" style={{ borderColor: C.softBorder, background: C.inputBg }}>
            <div className="min-w-0">
              <div className="text-[10px] leading-none" style={{ color: C.muted }}>{t('kyc.name')}</div>
              <div className="mt-1 truncate text-sm font-semibold leading-none" style={{ color: C.text }}>{submission.real_name}</div>
            </div>
            <div className="mx-3 h-9 w-px" style={{ background: C.statDividerAlt }} />
            <div className="min-w-0 text-right">
              <div className="text-[10px] leading-none" style={{ color: C.muted }}>{t('kyc.idNumber')}</div>
              <div className="mt-1 truncate font-mono text-sm font-semibold leading-none" style={{ color: C.gold }}>{submission.id_number}</div>
            </div>
          </div>
        )}
      </div>
    </ThemedPanel>
  );
}



export function KycSubmit() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [form, setForm] = useState({ real_name: '', id_type: 'id_card', id_number: '' });
  const [idTypeDropdownOpen, setIdTypeDropdownOpen] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  const idTypeOptions = ID_TYPES.map((item) => ({ value: item.value, label: t(item.key) }));

  const { data: submission, isLoading } = useQuery({
    queryKey: ['kyc-status'],
    queryFn: async () => {
      return kycAPI.getStatus();
    },
    staleTime: 30_000,
  });

  const submitMutation = useMutation({
    mutationFn: async () => {
      return kycAPI.submit(form as unknown as Parameters<typeof kycAPI.submit>[0]);
    },
    onSuccess: () => {
      setMsg(t('kyc.submitSuccess'));
      setError('');
      qc.invalidateQueries({ queryKey: ['kyc-status'] });
      qc.invalidateQueries({ queryKey: ['profile'] });
    },
    onError: (e: any) => {
      setError(e?.response?.data?.msg || e.message || t('common.error'));
      setMsg('');
    },
  });

  const status = submission?.status ?? 'none';
  const statusInfo = STATUS_KEYS[status] ?? STATUS_KEYS.none;
  const canSubmit = status === 'none' || status === 'rejected';

  return (
    <div className="relative min-h-full" style={{ background: C.pageBg }}>
      <div className="relative mx-auto min-h-full w-full max-w-[430px]">
        <ThemedPageHeader title={t('kyc.title')} onBack={() => navigate(-1)} theme={C} />
        <div className="flex flex-col gap-4 overflow-y-auto px-5 pt-[70px]" style={{ height: 'calc(100% - 70px)', paddingBottom: canSubmit ? '112px' : '12px' }}>
          <KycStatusPanel submission={submission} statusInfo={statusInfo} />

          {isLoading && <LoadingSkeleton variant="card" />}

          {canSubmit && (
            <ThemedPanel className="p-4">
              <div className="flex flex-col gap-6">   
                <div className="text-sm font-semibold" style={{ color: C.text }}>{status === 'rejected' ? t('kyc.resubmit') : t('kyc.submit')}</div>
                <div className="flex flex-col gap-5">
                  <ThemedField label={t('kyc.realName')} required>
                    <ThemedInput value={form.real_name} placeholder={t('kyc.realNamePlaceholder')} onChange={(e) => setForm({ ...form, real_name: e.target.value })} />
                  </ThemedField>
                  <ThemedField label={t('kyc.idType')} required>
                    <ThemedSelect
                      value={form.id_type}
                      options={idTypeOptions}
                      open={idTypeDropdownOpen}
                      onToggle={() => setIdTypeDropdownOpen((open) => !open)}
                      onSelect={(value) => {
                        setForm({ ...form, id_type: value });
                        setIdTypeDropdownOpen(false);
                      }}
                    />
                  </ThemedField>
                  <ThemedField label={t('kyc.idNumber')} required>
                    <ThemedInput value={form.id_number} placeholder={t('kyc.idNumberPlaceholder')} onChange={(e) => setForm({ ...form, id_number: e.target.value })} />
                  </ThemedField>
                  {msg && <ThemedNotice tone="success">{msg}</ThemedNotice>}
                  {error && <ThemedNotice tone="error">{error}</ThemedNotice>}
                </div>
              </div>
            </ThemedPanel>
          )}
        </div>
        {canSubmit && (
          <ThemedBottomActionBar>
            <ThemedSubmitButton disabled={!form.real_name || !form.id_number || submitMutation.isPending} onClick={() => submitMutation.mutate()}>
              {submitMutation.isPending ? '…' : t('kyc.submit')}
            </ThemedSubmitButton>
          </ThemedBottomActionBar>
        )}
      </div>
    </div>
  );
}

export default KycSubmit;
